import { DASHBOARD_URL } from "@/lib/constants";
import { stripLocaleFromPath } from "@/lib/i18n/routing";

export function sanitizeRegistrationSource(src: string): string {
  let normalized = src.toLowerCase().trim();
  normalized = normalized.replace(/\/+/g, "/").replace(/^\/|\/$/g, "");

  if (!normalized || normalized.length > 191) {
    return "";
  }

  if (!/^[a-z0-9._/-]+$/.test(normalized)) {
    return "";
  }

  return normalized;
}

export function detectRegistrationSource(
  pathname: string,
  override?: string | null,
): string {
  if (override) {
    const sanitized = sanitizeRegistrationSource(override);
    if (sanitized) {
      return sanitized;
    }
  }

  let path = stripLocaleFromPath(pathname);

  try {
    path = decodeURIComponent(path);
  } catch {
    // Keep the raw path when decoding fails.
  }

  path = path.toLowerCase().replace(/\/$/, "") || "/";
  if (path === "") {
    path = "/";
  }

  if (path === "/") {
    return "landing";
  }

  const exact: Record<string, string> = {
    "/pricing": "pricing",
    "/detailed-pricing": "detailed-pricing",
    "/affiliates": "affiliates",
    "/about-us": "about",
    "/our-data": "our-data",
    "/blog": "blog",
    "/payg-calculator": "payg-calculator",
    "/faq": "faq",
    "/contact": "contact",
    "/mediaroom": "mediaroom",
    "/search": "search",
    "/case-studies": "case-study",
    "/case-studies/all": "case-study",
    "/integrations/all": "integration/all",
    "/tools": "tools/all",
    "/tools/all": "tools/all",
    "/legal-and-security": "legal/all",
    "/legal-and-security/all": "legal/all",
  };

  if (exact[path]) {
    return exact[path];
  }

  const serviceMatch = path.match(/^\/services\/([a-z0-9._-]+)$/);
  if (serviceMatch) {
    return `service/${serviceMatch[1]}`;
  }

  const useCaseMatch = path.match(/^\/use-cases\/([a-z0-9._-]+)$/);
  if (useCaseMatch) {
    return `use-case/${useCaseMatch[1]}`;
  }

  const comparisonMatch = path.match(/^\/comparisons\/greip-vs-([a-z0-9._-]+)$/);
  if (comparisonMatch) {
    return `comparison/${comparisonMatch[1]}`;
  }

  const alternativeMatch = path.match(
    /^\/alternatives\/([a-z0-9._-]+)-alternatives$/,
  );
  if (alternativeMatch) {
    return `alternative/${alternativeMatch[1]}`;
  }

  const dictionaryMatch = path.match(/^\/blog\/dictionary\/(.+)-([0-9]+)$/);
  if (dictionaryMatch) {
    const slug = sanitizeRegistrationSource(dictionaryMatch[1]);
    return slug ? `dictionary/${slug}` : "blog";
  }

  const blogSectionMatch = path.match(/^\/blog\/section\/([a-z0-9._-]+)$/);
  if (blogSectionMatch) {
    return `blog-section/${blogSectionMatch[1]}`;
  }

  if (path.match(/^\/blog\/tags\/.+$/)) {
    return "blog";
  }

  if (path === "/blog/authors") {
    return "blog";
  }

  if (path.match(/^\/blog\/authors\/.+-([0-9]+)$/)) {
    return "blog";
  }

  const blogArticleMatch = path.match(/^\/blog\/(.+)-([0-9]+)$/);
  if (blogArticleMatch) {
    const slug = sanitizeRegistrationSource(blogArticleMatch[1]);
    return slug ? `blog/${slug}` : "blog";
  }

  const toolMatch = path.match(/^\/tools\/([a-z0-9._-]+)$/);
  if (toolMatch) {
    return `tools/${toolMatch[1]}`;
  }

  const caseStudyMatch = path.match(/^\/case-studies\/([a-z0-9._-]+)$/);
  if (caseStudyMatch) {
    return `case-study/${caseStudyMatch[1]}`;
  }

  const integrationMatch = path.match(/^\/integrations\/([a-z0-9._-]+)$/);
  if (integrationMatch) {
    return `integration/${integrationMatch[1]}`;
  }

  const legalMatch = path.match(/^\/legal-and-security\/([a-z0-9._-]+)$/);
  if (legalMatch) {
    return `legal/${legalMatch[1]}`;
  }

  return "landing";
}

type RegisterUrlOptions = {
  src?: string | null;
  pathname?: string;
  redirect?: string;
};

export function getRegisterUrl(options: RegisterUrlOptions = {}): string {
  const redirect =
    (options.redirect ?? "subscription").replace(/[^a-z0-9_-]/gi, "") ||
    "subscription";

  const source = options.pathname
    ? detectRegistrationSource(options.pathname, options.src)
    : options.src
      ? sanitizeRegistrationSource(options.src)
      : "landing";

  const url = new URL("/register", DASHBOARD_URL);
  url.searchParams.set("redirect", redirect);

  if (source) {
    url.searchParams.set("src", source);
  }

  return url.toString();
}
